import { memo, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BarChart3, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Loader2, Search, Building2, Gauge, Package, CalendarDays, Unlink, Upload, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { LevelBadge } from './MotivareTab';
import { shortLabel, isTotal, wd } from '@/components/motivare/LevelHistoryTable';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const MONTHS = ['', 'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);
const qty = (v) => (v == null ? '—' : Number.isInteger(v) ? v : v.toLocaleString('ro-RO', { maximumFractionDigits: 1 }));
const lei = (v) => (v == null ? '—' : `${v.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei`);
const dd = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : '');
const nextDay = (iso) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1); return d.toISOString().slice(0, 10); };
const wdd = (iso) => `${wd(iso)} ${dd(iso)}`;
const dayLabel = (r) => { const from = r.from_day ? nextDay(r.from_day) : r.start_day; return from && from !== r.day ? `${wdd(from)} – ${wdd(r.day)}` : wdd(r.day); };

const Chip = ({ label, value, sub, subClass = 'text-slate-400', strong = false, weak = false, testId }) => (
  <div className={`rounded border px-1.5 py-0.5 min-w-[64px] leading-tight ${weak ? 'bg-amber-50 border-amber-200' : 'bg-white'}`} data-testid={testId}>
    <div className="text-[9px] uppercase tracking-wide text-slate-400 truncate max-w-[120px]" title={label}>{label}</div>
    <div className={`text-xs ${strong || weak ? 'font-semibold' : ''} ${weak ? 'text-amber-800' : 'text-slate-800'}`}>{value}{sub != null && <span className={`ml-1 text-[9px] font-normal ${subClass}`}>{sub}</span>}</div>
  </div>
);

const LevelLine = ({ lv, kpiOrder, label = 'Level' }) => {
  if (!lv) return <div className="text-[11px] text-slate-400 italic flex items-center gap-1"><Gauge className="w-3 h-3" /> Fără raport Level la această raportare</div>;
  const ag = lv.reference?.agency?.values || {};
  const kpiKeys = kpiOrder.filter((c) => !isTotal(c.key)).map((c) => c.key);
  const cand = kpiKeys.filter((k) => lv.values?.[k] != null);
  const weak = cand.length ? cand.reduce((a, b) => (lv.values[b] < lv.values[a] ? b : a)) : null;
  return (
    <div className="flex flex-wrap items-stretch gap-1" data-testid="analiza-level-line">
      <div className="flex flex-col justify-center text-[11px] text-slate-500 w-28 shrink-0 whitespace-nowrap leading-tight">
        <span className="flex items-center gap-1"><Gauge className="w-3 h-3 text-emerald-600" /> {label}{lv.report_date || lv.day ? <span className="text-[10px] text-slate-600 font-medium">· {wdd(lv.report_date || lv.day)}</span> : null}</span>
        {lv.day && lv.report_date && lv.report_date !== lv.day && <span className="text-[9px] text-slate-400 pl-4">măsoară până la {dd(lv.day)}</span>}
      </div>
      {kpiOrder.map((c) => {
        const v = lv.values?.[c.key];
        const a = ag[c.key];
        const d = v != null && a != null ? Math.round((v - a) * 1000) / 10 : null;
        return <Chip key={c.key} label={shortLabel(c.key, c.label)} value={pct(v)} weak={weak === c.key} strong={isTotal(c.key)}
          sub={d == null ? null : `${d > 0 ? '+' : ''}${d} vs ag.`} subClass={d == null ? '' : d >= 0 ? 'text-emerald-600' : 'text-red-600'} />;
      })}
      <div className="flex items-center px-1"><LevelBadge level={lv.level} /></div>
    </div>
  );
};

function AgencyPanel({ ag, columns, kpiOrder }) {
  const [open, setOpen] = useState(true);
  const stopCol = columns.find((c) => /stop/i.test(c.label)) || columns[0];
  const hasLevel = ag.level != null || ag.rows.some((r) => r.level);
  return (
    <Card className="border-slate-800/30 bg-slate-50/60" data-testid="analiza-agency">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-4 px-3 py-2 text-left hover:bg-slate-100/60 rounded-lg" data-testid="analiza-agency-toggle">
        <div className="min-w-[180px]">
          <div className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> Agenție</div>
          <div className="text-sm font-semibold">{ag.name || 'IL_SLOBOZIA'}</div>
          <div className="text-xs text-slate-500">{ag.agents_count} agenți asociați</div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 flex-1 text-sm">
          <LevelBadge level={ag.level} testId="analiza-agency-level" />
          <span className="text-slate-600">OTD <b>{pct(ag.otd)}</b></span>
          <span className="text-slate-600">Zile <b>{qty(ag.days)}</b></span>
          {stopCol && <span className="text-slate-600">{stopCol.label}: <b data-testid="analiza-agency-total">{qty(ag.totals[stopCol.key])}</b>{ag.per_day[stopCol.key] != null && <span className="text-xs text-slate-400"> · {qty(ag.per_day[stopCol.key])}/zi</span>}</span>}
          <span className="text-xs text-slate-400 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {ag.report_days} raportări</span>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {open && (
        <CardContent className="pt-0 pb-3 space-y-1.5" data-testid="analiza-agency-body">
          {!hasLevel && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5" data-testid="analiza-agency-missing">Level-ul agenției apare după următorul import al raportului Level.</div>}
          <div className="flex flex-wrap gap-1 px-2" data-testid="analiza-agency-summary">
            <div className="flex items-center text-[11px] text-slate-500 w-24 shrink-0">Total lună</div>
            {columns.map((c) => <Chip key={c.key} label={c.label} value={qty(ag.totals[c.key])} sub={ag.per_day[c.key] != null ? `${qty(ag.per_day[c.key])}/zi` : null} strong />)}
            <ExtraChips extra={ag.extra} />
          </div>
          {ag.rows.length === 0 ? (
            <div className="text-sm text-slate-500 italic">Istoricul pe zile începe cu următoarea raportare importată.</div>
          ) : ag.rows.map((r) => (
            <div key={r.day} className="rounded border bg-white px-2 py-1.5 space-y-1" data-testid={`analiza-agency-day-${r.day}`}>
              <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-1.5"><CalendarDays className="w-3 h-3" /> {dayLabel(r)}</div>
              <LevelLine lv={r.level} kpiOrder={kpiOrder} />
              <ActivityLine row={r} columns={columns} />
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}

const ActivityLine = ({ row, columns }) => (
  <div className="flex flex-wrap items-stretch gap-1" data-testid="analiza-activity-line">
    <div className="flex items-center gap-1 text-[11px] text-slate-500 w-28 shrink-0 whitespace-nowrap"><Package className="w-3 h-3 text-blue-600" /> Activitate{row.days_diff ? <span className="text-[9px] text-slate-400">· {qty(row.days_diff)} z</span> : null}</div>
    {columns.map((c) => <Chip key={c.key} label={c.label} value={qty(row.values[c.key])} sub={`/ ${qty(row.cumulative[c.key])}`} strong />)}
    <Chip label="Km zi" value={row.km == null ? '—' : qty(row.km)} strong testId="chip-km" />
    <Chip label="PPV zi" value={row.ppv ? qty(row.ppv.ppv) : '—'} sub={row.ppv ? `/ ${qty(row.ppv.ppv_cum)}` : 'fără raport PPV'} strong testId="chip-ppv" />
    <Chip label="Plată zi" value={row.ppv ? lei(row.ppv.plata) : '—'} sub={row.ppv ? `/ ${lei(row.ppv.plata_cum)}` : null} strong testId="chip-plata" />
  </div>
);
const ExtraChips = ({ extra }) => extra ? (
  <>
    <Chip label="Km total" value={extra.km == null ? '—' : qty(extra.km)} strong testId="chip-km-total" />
    <Chip label="PPV total" value={extra.ppv ? qty(extra.ppv.ppv) : '—'} sub={extra.ppv ? `la ${dd(extra.ppv.day)}` : 'fără raport PPV'} strong testId="chip-ppv-total" />
    <Chip label="Total perioadă" value={extra.ppv ? lei(extra.ppv.plata) : '—'} strong testId="chip-plata-total" />
  </>
) : null;

function AgentPanel({ a, columns, kpiOrder }) {
  const [open, setOpen] = useState(false);
  const stopCol = columns.find((c) => /stop/i.test(c.label)) || columns[0];
  return (
    <Card className={a.employee_id ? '' : 'opacity-70'} data-testid={`analiza-agent-${a.indicativ}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-4 px-3 py-1.5 text-left hover:bg-slate-50 rounded-lg" data-testid={`analiza-toggle-${a.indicativ}`}>
        <div className="min-w-[180px]">
          <div className="font-mono text-xs text-slate-500">{a.indicativ}</div>
          <div className="text-sm font-medium">{a.name}</div>
          <div className="text-xs text-slate-500">{a.employee_name || <span className="italic flex items-center gap-1"><Unlink className="w-3 h-3" /> neasociat</span>}</div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 flex-1 text-sm">
          <span className="flex items-center gap-1.5"><LevelBadge level={a.level} testId={`analiza-level-${a.indicativ}`} />{a.backup && a.level != null && <span className="text-[10px] text-violet-700">back-up</span>}</span>
          <span className="text-slate-600">OTD <b>{pct(a.otd)}</b></span>
          <span className="text-slate-600">Zile <b>{qty(a.days)}</b></span>
          {stopCol && <span className="text-slate-600">{stopCol.label}: <b data-testid={`analiza-total-${a.indicativ}`}>{qty(a.totals[stopCol.key])}</b>{a.per_day[stopCol.key] != null && <span className="text-xs text-slate-400"> · {qty(a.per_day[stopCol.key])}/zi</span>}</span>}
          <span className="text-xs text-slate-400 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {a.report_days} raportări</span>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {open && (
        <CardContent className="pt-0 pb-3 space-y-1.5" data-testid={`analiza-body-${a.indicativ}`}>
          <div className="flex flex-wrap gap-1 px-2" data-testid={`analiza-summary-${a.indicativ}`}>
            <div className="flex items-center text-[11px] text-slate-500 w-24 shrink-0">Total lună</div>
            {columns.map((c) => <Chip key={c.key} label={c.label} value={qty(a.totals[c.key])} sub={a.per_day[c.key] != null ? `${qty(a.per_day[c.key])}/zi` : null} strong />)}
            <ExtraChips extra={a.extra} />
          </div>
          {a.rows.length === 0 ? (
            <div className="text-sm text-slate-500 italic" data-testid={`analiza-empty-${a.indicativ}`}>Istoricul pe zile începe cu următoarea raportare importată.</div>
          ) : a.rows.map((r) => (
            <div key={r.day} className="rounded border px-2 py-1.5 space-y-1" data-testid={`analiza-day-${a.indicativ}-${r.day}`}>
              <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-1.5"><CalendarDays className="w-3 h-3" /> {dayLabel(r)}{dayLabel(r).includes('–') && <span className="font-normal text-slate-400">· activitate cumulată pe mai multe zile</span>}</div>
              <LevelLine lv={r.level} kpiOrder={kpiOrder} />
              <ActivityLine row={r} columns={columns} />
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}

const MotivareAnalizaTab = memo(function MotivareAnalizaTab() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const uploadPpv = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = (await axios.post(`${API_URL}/motivare/ppv-import`, fd, auth())).data;
      toast.success(`Raport PPV încărcat pentru ${dd(r.ppv_day)} · ${r.matched} curieri · ${lei(r.total_perioada)} cumulat`);
      if (r.year !== year || r.month !== month) { setYear(r.year); setMonth(r.month); } else load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Raportul PPV nu a putut fi încărcat'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await axios.get(`${API_URL}/motivare/analiza`, { ...auth(), params: { year, month } })).data); }
    catch { toast.error('Nu s-a putut încărca analiza'); }
    finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const shift = (d) => { let m = month + d, y = year; if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; } setMonth(m); setYear(y); };
  const s = q.trim().toLowerCase();
  const agents = (data?.agents || []).filter((a) => !s || a.indicativ.toLowerCase().includes(s) || (a.name || '').toLowerCase().includes(s) || (a.employee_name || '').toLowerCase().includes(s));

  return (
    <div className="space-y-4" data-testid="analiza-tab-content">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><BarChart3 className="w-5 h-5 text-slate-600" /> Analiză</h2>
          <p className="text-sm text-slate-500">Pe fiecare agent: Level-ul și activitatea zilnică (diferența dintre raportări), cu referința agenției</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => uploadPpv(e.target.files?.[0])} data-testid="analiza-ppv-input" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="analiza-ppv-upload">{uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Încarcă raport PPV</Button>
        <div className="flex items-center gap-1 border rounded-lg px-1 bg-white">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="analiza-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium min-w-[140px] text-center" data-testid="analiza-month-label">{MONTHS[month]} {year}</span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="analiza-next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2 top-2" />
          <Input className="h-8 w-64 pl-8 text-sm bg-white" placeholder="Caută indicativ / nume / angajat..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="analiza-search" />
        </div>
        {data && <span className="text-xs text-slate-500" data-testid="analiza-report-days">{data.report_days} raportări în lună · {agents.length} agenți</span>}
        {data && (
          <span className="text-xs flex items-center gap-1 flex-wrap" data-testid="analiza-ppv-status">
            <Coins className="w-3.5 h-3.5 text-amber-600" />
            {data.ppv_status.days.length === 0 ? <span className="text-slate-500">Niciun raport PPV încărcat pentru această lună.</span> : (
              <><span className="text-slate-600">Rapoarte PPV: <b>{data.ppv_status.days.map(dd).join(', ')}</b></span>
                {data.ppv_status.missing.length > 0 && <span className="text-amber-700">· lipsesc: {data.ppv_status.missing.map(dd).join(', ')}</span>}</>
            )}
          </span>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 p-8"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă...</div>
      ) : agents.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-slate-500" data-testid="analiza-empty">Nicio raportare pentru această lună.</CardContent></Card>
      ) : (
        <div className="space-y-1">
          {data.agency && <AgencyPanel ag={data.agency} columns={data.columns} kpiOrder={data.kpi_order} />}
          {agents.map((a) => <AgentPanel key={a.agent_id} a={a} columns={data.columns} kpiOrder={data.kpi_order} />)}
        </div>
      )}
    </div>
  );
});

export default MotivareAnalizaTab;
